import React, { FC } from 'react';
import {
  HStack,
  useRadioGroup,
  RadioGroup,
  Radio,
  Text,
  VStack,
  Box,
  VisuallyHidden,
} from '@chakra-ui/react';
import { useCheckoutDispatch } from '@olo-web/client-state';
import { usePaymentMethods } from '@templates/Checkout/common/hooks/usePaymentMethods';
import { EPaymentMethods } from '@olo-web/types/enums/paymentMethods.enum';
import { PaymentMethodRadioCard } from './PaymentMethodRadioCard.ui';
import { getPaymentMethodAriaLabel } from './getPaymentMethodAriaLabel';
import { useSendEvent } from '@olo-web/utils/common/hooks';
import { EAnalyticsEventNames } from '@olo-web/types/enums';

export const PaymentMethodRadioGroup: FC = () => {
  const { paymentMethods, selectedPaymentMethod, selectedPaymentMethodDisplay, isCreditCardOnly } =
    usePaymentMethods();
  const checkoutDispatch = useCheckoutDispatch();
  const { sendEvent } = useSendEvent();
  const { getRootProps, getRadioProps } = useRadioGroup({
    value: selectedPaymentMethod,
    name: 'paymentMethod',
    onChange: (value: EPaymentMethods) => {
      sendEvent(EAnalyticsEventNames.SWITCH_PAYMENT_METHOD, {
        googleAnalytics: {
          paymentType: value,
        },
      });
      checkoutDispatch({
        type: 'SELECT_PAYMENT_METHOD',
        payload: value,
      });
    },
  });

  if (isCreditCardOnly) return null;
  return (
    <VStack spacing={4} alignItems="stretch">
      <Box as="fieldset" border="none" p={0} m={0} w="100%">
        <VisuallyHidden as="legend">Payment method</VisuallyHidden>
        <RadioGroup w="100%">
          <HStack w="fit-content" spacing="0" {...getRootProps()}>
            {paymentMethods.map((paymentMethod) => {
              const radio = getRadioProps({
                value: paymentMethod.value,
              });

              return (
                <PaymentMethodRadioCard
                  key={paymentMethod?.title}
                  value={radio.value || ''}
                  {...radio}
                >
                  <Radio
                    //@ts-ignore
                    isChecked={radio.isChecked}
                    onChange={() => {
                      radio.onChange(radio.value);
                    }}
                    inputProps={{ 'aria-label': getPaymentMethodAriaLabel(paymentMethod) }}
                    data-testid={`${paymentMethod.title}-radio-button`}
                  >
                    {paymentMethod.radioLabel}
                  </Radio>
                </PaymentMethodRadioCard>
              );
            })}
          </HStack>
        </RadioGroup>
      </Box>
      {selectedPaymentMethodDisplay?.note && (
        <Text textStyle={'p'} aria-hidden>
          {selectedPaymentMethodDisplay.note}
        </Text>
      )}
    </VStack>
  );
};
